<template>
    <div :class="s.container">
        <span :class="[theme.quickbalances.subheaderText, s.subheader]">
            Placement
        </span>
        <div :class="s.column">
            <div
                v-for="(placement, index) in isDeFi ? defiPlacementsList : allowedPlacementsList"
                :key="placement.id"
                :class="[
                    s.pointer,
                    {
                        [theme.quickbalances.activeBackground]: isWorkspace
                            ? activeWorkspacePlacementIndex === index
                            : currentPlacementIndex === index,
                        [s.notAllowed]: !getIsPlacementAvailable(placement.name),
                    }
                ]"
                @click="setActivePlacement(index)"
            >
                <div :class="s.row">
                    <PlacementIcon
                        :placement="placement.name"
                        :size="14"
                        :class="{ [s.disabled]: !getIsPlacementAvailable(placement.name) }"
                    />
                    <span
                        :class="[
                            theme.quickbalances.placementText,
                            {
                                [theme.quickbalances.active]: isWorkspace ? activeWorkspacePlacementIndex === index : currentPlacementIndex === index,
                                [s.disabled]: !getIsPlacementAvailable(placement.name)
                            }
                        ]"
                    >
                        {{ placement.name.trim() }}
                    </span>
                    <Icon
                        :class="[s.violet, { [s.disabled]: !getIsPlacementAvailable(placement.name) }]"
                        icon="check"
                        v-if="isWorkspace ? activeWorkspacePlacementIndex === index : currentPlacementIndex === index"
                        small
                    />
                    <Icon
                        v-if="!getIsPlacementAvailable(placement.name)"
                        icon="clock"
                        :class="s.clock"
                    />
                    <Icon
                        v-else-if="!getIsPlacementLinked(placement.name) && activeAccountId !== 'DEMOACC'"
                        icon="link"
                        :class="s.link"
                        @clicked="linkPlacement(placement.name)"
                    />
                </div>
                <span :class="[s.totalRow, { [s.disabled]: !getIsPlacementAvailable(placement.name) }]">
                    ≈{{ quotationAssetCharacter }}{{ totalPlacementBalance(placement.name).toFixed(quotationAssetPrecision) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { RELEASED_PLATFORMS } from 'Store/modules/Placements';
import { setAccountPortfolioActivePlacementId } from 'Store/v2/Balances';
import theme from 'Theme';
import Icon from 'UI/Icon.vue';
import { calculatePrecision } from 'Lib/utils/quotationAssetPrecisionCalculator';
import { SET_PLACEMENT_NAME } from 'Store/v2/LinkPlacement';

export default {
    name: 'AccountPortfolio.ChoosePlacement',
    props: {
        isDeFi: {
            type: Boolean,
            default: false,
        },
        isWorkspace: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        Icon,
    },
    data() {
        return {
            activeWorkspacePlacementIndex: 1,
            theme,
        };
    },
    computed: {
        ...mapGetters({
            placementsList: 'Placements/placements',
            accountPortfolioActivePlacementId: 'Balances/GET_ACCOUNT_PORTFOLIO_ACTIVE_PLACEMENT_ID',
            quotationAssetCharacter: 'Assets/GET_QUOTATION_ASSET_CHARACTER',
            quotationAssetSymbol: 'Assets/GET_QUOTATION_ASSET_SYMBOL',
            activeAccountId: 'Accounts/activeAccountID',
        }),
        defiPlacementsList() {
            return this.placementsList.filter((p) => p.name === 'Single Broker');
        },
        filteredPlacementsList() {
            return this.placementsList.filter((p) => p.type.indexOf('decentralized') === -1);
        },
        allowedPlacementsList() {
            // eslint-disable-next-line no-prototype-builtins
            return this.filteredPlacementsList.filter((p) => RELEASED_PLATFORMS.hasOwnProperty(p.name) || p.name === 'Single Broker');
        },
        currentPlacementIndex() {
            return this.isDeFi ? 0 : this.allowedPlacementsList.findIndex((placement) => Number(placement.id) === Number(this.accountPortfolioActivePlacementId));
        },
        balances() {
            return this.$store.state.Balances.balances;
        },
        activeAccountId() {
            return this.$store.state.Accounts.activeAccountID;
        },
        quotationAssetPrecision() {
            return calculatePrecision(this.quotationAssetSymbol);
        },
    },
    methods: {
        async setActivePlacement(index) {
            const placementStatuses = this.$store.state.Placements.maintenanceStatuses;

            if (this.isWorkspace) {
                if (placementStatuses.get(this.allowedPlacementsList[index].name)
                    || this.allowedPlacementsList[index].name === 'Single Broker') {
                    this.activeWorkspacePlacementIndex = index;
                    this.$emit('set-placement', this.allowedPlacementsList[this.activeWorkspacePlacementIndex].id);
                }
            } else {
                if (this.isDeFi) {
                    if (!placementStatuses.get(this.defiPlacementsList[index].name)
                        && this.defiPlacementsList[index].name !== 'Single Broker') {
                        return;
                    }
                } else if (!placementStatuses.get(this.allowedPlacementsList[index].name)
                    && this.allowedPlacementsList[index].name !== 'Single Broker') {
                    return;
                }
                await this.$store.dispatch(setAccountPortfolioActivePlacementId(this.isDeFi ? this.defiPlacementsList[index].id : this.allowedPlacementsList[index].id));
            }
        },
        totalPlacementBalance(name) {
            return this.balances.reduce((accum, { accountId, placementName, quotationAssetSymbols, quotations, total }) => {
                if (placementName !== name || accountId !== this.activeAccountId) {
                    return accum;
                }
                const quotationAssetSymbolIndex = quotationAssetSymbols.findIndex((symbol) => symbol === this.quotationAssetSymbol);
                if (quotationAssetSymbolIndex === -1) {
                    return accum;
                }
                return accum + (Number(quotations[quotationAssetSymbolIndex]) * total);
            }, 0);
        },
        getIsPlacementDisabled(placementName) {
            return (!this.$store.state.Placements.maintenanceStatuses.get(placementName) && placementName !== 'Single Broker')
                || (!this.$store.getters['Accounts/isPlacementLinkedToActiveAccount'](placementName) && placementName !== 'Single Broker');
        },
        getIsPlacementAvailable(placementName) {
            return this.$store.state.Placements.maintenanceStatuses.get(placementName) || placementName === 'Single Broker';
        },
        getIsPlacementLinked(placementName) {
            return this.$store.getters['Accounts/isPlacementLinkedToActiveAccount'](placementName) || placementName === 'Single Broker';
        },
        async linkPlacement(placementName) {
            const placement = this.$store.state.Placements.placements.find((p) => p.name.toLowerCase() === placementName.toLowerCase());
            if (placement && placement.needExternalKyc) {
                this.$store.commit(SET_PLACEMENT_NAME(placementName));
                this.$modal.show('confirmLinkModal');
            } else {
                await this.$store.dispatch('Accounts/linkPlacement', placementName);
            }
        },
    },
    async mounted() {
        if (this.isDeFi) {
            await this.setActivePlacement(0);
            this.$emit('set-placement', this.defiPlacementsList[0].id);
            return;
        }
        if (this.isWorkspace) {
            this.$emit('set-placement', this.allowedPlacementsList[this.activeWorkspacePlacementIndex].id);
        }
    },
    watch: {
        async defiPlacementsList() {
            if (this.isDeFi) {
                await this.setActivePlacement(0);
            }
        },
    },
};
</script>

<style lang="postcss" module="s">
.container {
    margin-right: var(--m-s);
    padding: var(--m-s) 0 var(--m-m) var(--m-s);
    max-height: 100%;
    overflow-y: scroll;
}
.pointer {
    cursor: pointer;
}
.subheader {
    padding-left: var(--m-s);
    margin-bottom: var(--m-s);
}
.column {
    display: flex;
    flex-direction: column;
    row-gap: var(--m-s);
}
.row {
    display: flex;
    align-items: center;
    column-gap: var(--m-xs);
    padding: var(--m-s) var(--m-s) var(--m-xs) var(--m-s);
}
.violet {
    color: var(--cl-violet);
}
.totalRow {
    display: flex;
    font-weight: var(--fw-semibold);
    font-size: var(--fs-m);
    line-height: var(--fs-m);
    color: var(--cl-gray);
    padding: 0 var(--m-s) var(--m-s) calc(14px + var(--m-xs) + var(--m-s));
}
.notAllowed {
    cursor: not-allowed;
}
.disabled {
    opacity: .4;
}
.clock {
    margin-left: auto;
    color: var(--cl-gray);
}
.link {
    margin-left: auto;
    color: var(--cl-violet);
    cursor: pointer;
}
</style>
